import { Timestamp } from 'firebase/firestore';

// Convert Firestore Timestamp to ISO string
export const timestampToISO = (timestamp: Timestamp | null | undefined): string => {
  if (!timestamp) return new Date().toISOString();
  return timestamp.toDate().toISOString();
};

// Type guard to check if an object is a Firestore Timestamp
const isFirestoreTimestamp = (obj: any): obj is Timestamp => {
  return obj && 
         typeof obj === 'object' && 
         'toDate' in obj && 
         typeof obj.toDate === 'function' &&
         'seconds' in obj &&
         'nanoseconds' in obj;
};

// Convert ISO string to Firestore Timestamp
export const isoToTimestamp = (isoString: string): Timestamp => {
  return Timestamp.fromDate(new Date(isoString));
};

// Convert Firestore document to app data type
export const convertFirestoreDoc = <T>(doc: any): T => {
  const data = doc.data();
  const converted: any = { ...data, id: doc.id };
  
  // Recursively convert all timestamp fields to ISO strings
  const convertTimestamps = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return obj;
    
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (isFirestoreTimestamp(value)) {
        // This is a Firestore Timestamp
        result[key] = timestampToISO(value);
      } else if (Array.isArray(value)) {
        result[key] = value.map(convertTimestamps);
      } else if (value && typeof value === 'object') {
        result[key] = convertTimestamps(value);
      } else {
        result[key] = value;
      }
    }
    return result;
  };
  
  return convertTimestamps(converted) as T;
};

// Convert app data to Firestore data
export const convertToFirestore = (data: any): any => {
  const converted: any = { ...data };
  
  // Remove id field (Firestore generates this)
  delete converted.id;
  
  // Recursively convert ISO strings to timestamps
  const convertToTimestamps = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return obj;
    
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key === 'id') continue; // Skip id fields
      
      if (typeof value === 'string' && (key === 'createdAt' || key === 'updatedAt' || key === 'timestamp' || key === 'addedAt')) {
        // Convert ISO strings to timestamps for specific fields
        result[key] = isoToTimestamp(value);
      } else if (Array.isArray(value)) {
        result[key] = value.map(convertToTimestamps);
      } else if (value && typeof value === 'object') {
        result[key] = convertToTimestamps(value);
      } else {
        result[key] = value;
      }
    }
    return result;
  };
  
  return convertToTimestamps(converted);
};

// Generate a unique ID (fallback for client-side operations)
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};
